import React, { useState, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Button } from './ui/button';
import { ChevronsUpDown, Check, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Shared searchable combobox built on cmdk (Popover + Command).
 *
 * Props:
 *  - items: array of options, each with `_key` (id) and `_searchStr` (lowercased searchable string).
 *  - value: currently selected `_key`.
 *  - onSelect(key): called when an item is picked.
 *  - placeholder / searchPlaceholder / emptyText: UI strings.
 *  - renderItem(item, isSelectedDisplay): renders an option row (and the trigger label when selected).
 *  - testId: data-testid for the trigger button.
 *  - addNewLabel: optional label to render an "add new" action at the bottom of the list (e.g. "+ Add new employee").
 *  - onAddNew(): optional callback fired when the add-new action is clicked. The popover closes automatically.
 */
export function SearchableCombobox({
  items,
  value,
  onSelect,
  placeholder,
  searchPlaceholder,
  emptyText,
  renderItem,
  testId,
  addNewLabel,
  onAddNew,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const listRef = useRef(null);
  const selected = items.find(i => i._key === value);

  // Task 1 fix: whenever the search term changes (i.e., filtered list contents change),
  // reset the CommandList scroll position to the top so users always see the highest-relevance
  // matches. Without this, cmdk preserves the previous scrollTop across keystrokes which causes
  // the visible viewport to land at the bottom of the new (shorter) list.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [search, open]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal h-auto min-h-[2.5rem] whitespace-normal text-left"
          data-testid={testId}
        >
          {selected ? renderItem(selected, true) : <span className="text-slate-500">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          {/* Sticky "Add new" action — rendered ABOVE the scrollable CommandList so it's
              always visible regardless of result-list length. Lives outside CommandList
              so it never scrolls away with the results. */}
          {onAddNew && addNewLabel && (
            <div className="p-2 border-b bg-slate-50">
              <button
                type="button"
                onClick={() => { setOpen(false); setSearch(''); onAddNew(); }}
                data-testid={testId ? `${testId}-add-new` : 'combobox-add-new'}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md
                           bg-brand text-brand-contrast text-sm font-medium
                           hover:bg-brand-hover active:bg-brand-hover transition-colors
                           focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1"
              >
                <Plus className="w-4 h-4 flex-shrink-0" />
                {addNewLabel}
              </button>
            </div>
          )}
          <CommandList ref={listRef}>
            <CommandEmpty>
              <div className="py-3 text-sm text-slate-500 text-center">{emptyText}</div>
            </CommandEmpty>
            <CommandGroup>
              {items.map((item, i) => (
                <CommandItem
                  // Some callers build `_key` from data that can repeat; the index
                  // keeps React and cmdk from collapsing two rows into one.
                  key={`${item._key}-${i}`}
                  value={`${item._searchStr} ${item._key}-${i}`}
                  onSelect={() => { onSelect(item._key); setOpen(false); setSearch(''); }}
                >
                  <Check className={cn('mr-2 h-4 w-4 flex-shrink-0', value === item._key ? 'opacity-100' : 'opacity-0')} />
                  {renderItem(item, false)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableCombobox;
